import { sfx, setVolume, volume, nowPlaying } from "../lib/sound";
import { launchShowPref, setLaunchShowPref } from "../lib/launchPref";
import { useEffect, useState } from "react";
import { cloudList, cloudLoad, cloudSave, leaveSeason } from "../online/session";
import { Icon, Sheet } from "@bd/ui-kit";
import { host, me, useGame } from "../store";
import { deleteSave, listSaves, loadGame, saveGame, type SaveMeta } from "../lib/save";
import { dateOf } from "@bd/sim";
import { t, tdate } from "../i18n";
import { LangSwitch } from "../ui/LangSwitch";

export function Menu() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const toast = useGame((s) => s.toast);
  const quality = useGame((s) => s.quality);
  const textScale = useGame((s) => s.textScale);
  const online = useGame((s) => s.online);
  const [saves, setSaves] = useState<SaveMeta[]>([]);
  const [cloud, setCloud] = useState<{ slot: string; label: string; week: number }[]>([]);
  const refresh = () => {
    void listSaves().then(setSaves);
    void cloudList().then(setCloud);
  };
  useEffect(refresh, []);
  const toCloud = async () => {
    const e = await cloudSave("cloud1", me(game)!.name, game.week, game);
    toast(e ? { title: t("Not saved to the cloud"), text: e, tone: "warn" } : { title: t("Saved to the cloud"), text: t("Load it on any device you sign in on."), tone: "good" });
    refresh();
  };
  const fromCloud = async (slot: string) => {
    const g = await cloudLoad(slot);
    if (g) {
      host.load(g as typeof game);
      set({ screen: null, selectedRoom: null, closeup: null, onboarding: 4 });
    }
  };
  const save = async (slot: string) => {
    await saveGame(slot, game);
    toast({ title: t("Saved"), text: `${me(game)!.name}, ${tdate(dateOf(game.week).label)}`, tone: "good" });
    refresh();
  };
  const load = async (slot: string) => {
    const g = await loadGame(slot);
    if (g) {
      host.load(g);
      set({ screen: null, selectedRoom: null, closeup: null, onboarding: 4 });
    }
  };
  return (
    <Sheet title={t("Menu")} onClose={() => set({ screen: null })} testId="menu">
      <div className="grid2">
        <div className="stack">
          {game.finale && <button className="btn primary" onClick={() => set({ screen: { kind: "finale" } })} data-testid="open-finale"><Icon name="trophy" size={18} />{t("The 2035 awards")}</button>}
          <div className="menu-row">
            <button className="btn" style={{ flex: 1 }} onClick={() => set({ screen: { kind: "achievements" } })} data-testid="open-achievements"><Icon name="star" size={18} color="var(--gold)" />{t("Achievements")}<span className="num muted">{me(game)!.achievements.length}</span></button>
            <button className="btn" onClick={() => set({ screen: { kind: "feedback" } })} title={t("Send feedback")} data-testid="open-feedback"><Icon name="feedback" size={18} />{t("Feedback")}</button>
          </div>
          {online ? (
            <div className="card spread" style={{ gap: 8 }} title={t("Online seasons save every move by themselves.")}>
              <strong className="ellipsis">{online.name}</strong>
              <button className="btn small" onClick={() => { leaveSeason(); location.href = `${location.pathname}?online=1`; }} data-testid="leave-season"><Icon name="back" size={16} />{t("Lobby")}</button>
            </div>
          ) : (<>
          <div className="menu-row" role="group" aria-label={t("Save")}>
            {[1, 2, 3].map((n) => (
              <button key={n} className={`btn ${n === 1 ? "primary" : ""}`} style={{ flex: 1, minWidth: 0 }} onClick={() => save(`slot${n}`)} title={t("Save to slot {n}", { n })} aria-label={t("Save to slot {n}", { n })} data-testid={`save-${n}`}><Icon name="save" size={18} />{n}</button>
            ))}
            <button className="btn" style={{ flex: 1.4, minWidth: 0 }} onClick={() => void toCloud()} title={t("Save to the cloud")} data-testid="save-cloud"><Icon name="save" size={18} />{t("Cloud")}</button>
          </div>
          {saves.map((s) => (
            <div key={s.slot} className="card spread" style={{ padding: "6px 8px 6px 12px" }}>
              <span className="ellipsis" style={{ fontSize: "0.9rem" }} title={`${s.slot === "autosave" ? t("Autosave") : t("Slot {n}", { n: s.slot.replace("slot", "") })} · ${s.company}`}><strong>{s.slot === "autosave" ? <Icon name="replay" size={14} style={{ verticalAlign: -2 }} /> : s.slot.replace("slot", "")}</strong> · {tdate(dateOf(s.week).label)}</span>
              <span className="row" style={{ gap: 2, flex: "none" }}>
                <button className="btn small" onClick={() => load(s.slot)} data-testid={`load-${s.slot}`}>{t("Load")}</button>
                {s.slot !== "autosave" && <button className="icon-btn" style={{ width: 32, height: 32 }} onClick={async () => { await deleteSave(s.slot); refresh(); }} aria-label={t("Delete")} title={t("Delete")}><Icon name="close" size={14} color="var(--muted)" /></button>}
              </span>
            </div>
          ))}
          {cloud.map((c) => (
            <div key={c.slot} className="card spread" style={{ padding: "6px 8px 6px 12px" }}>
              <span className="ellipsis" style={{ fontSize: "0.9rem" }}><strong>{t("Cloud")}</strong> · {c.label} · {tdate(dateOf(c.week).label)}</span>
              <button className="btn small" onClick={() => void fromCloud(c.slot)} data-testid={`load-${c.slot}`}>{t("Load")}</button>
            </div>
          ))}
          </>)}
        </div>
        <div className="stack">
          <LangSwitch />
          <SoundSettings />
          <label className="spread">
            <span>{t("Graphics")}</span>
            <select aria-label={t("Graphics")} data-testid="graphics" value={quality} onChange={(e) => { localStorage.setItem("bd:quality", e.target.value); set({ quality: e.target.value as "high" | "low" }); }}>
              <option value="high">{t("High")}</option>
              <option value="low">{t("Low (faster)")}</option>
            </select>
          </label>
          <label className="spread">
            <span>{t("Text size")}</span>
            <select aria-label={t("Text size")} value={textScale} onChange={(e) => { localStorage.setItem("bd:textScale", e.target.value); set({ textScale: Number(e.target.value) }); }}>
              <option value={0.9}>{t("Small")}</option>
              <option value={1}>{t("Normal")}</option>
              <option value={1.15}>{t("Large")}</option>
              <option value={1.3}>{t("Extra large")}</option>
            </select>
          </label>
          <div className="menu-keys" title={t("Space pause · 1–4 speed · R rotate · Esc back")}>
            <span><kbd>Space</kbd><Icon name="pause" size={12} /></span>
            <span><kbd>1–4</kbd><Icon name="ff" size={12} /></span>
            <span><kbd>R</kbd><Icon name="replay" size={12} /></span>
            <span><kbd>Esc</kbd><Icon name="back" size={12} /></span>
          </div>
          <button className="btn ghost" style={{ alignSelf: "flex-start", color: "var(--bad)" }} title={t("Quit to title")} onClick={() => { if (confirm(t("Quit? Anything since the last autosave is lost."))) location.reload(); }}><Icon name="back" size={16} />{t("Quit")}</button>
        </div>
      </div>
    </Sheet>
  );
}

/** Music and effect volume, and when launch day plays its full show. */
function SoundSettings() {
  const [m, setM] = useState(volume.music);
  const [f, setF] = useState(volume.sfx);
  const [show, setShow] = useState(launchShowPref());
  const song = nowPlaying();
  return (
    <div className="card stack" style={{ gap: 8 }} data-testid="sound-settings">
      <label className="spread" style={{ gap: 10 }} title={song ? t("Now playing: {song}", { song }) : undefined}><span>{t("Music")}</span><input type="range" className="slider" min={0} max={1} step={0.05} value={m} onChange={(e) => { const v = Number(e.target.value); setM(v); setVolume("music", v); }} aria-label={t("Music volume")} /></label>
      <label className="spread" style={{ gap: 10 }}><span>{t("Effects")}</span><input type="range" className="slider" min={0} max={1} step={0.05} value={f} onChange={(e) => { const v = Number(e.target.value); setF(v); setVolume("sfx", v); sfx("click"); }} aria-label={t("Effects volume")} /></label>
      <label className="spread" style={{ gap: 10 }} title={t("Auto: the first 10 games, then big boxes and epics")}>
        <span>{t("Launch show")}</span>
        <select value={show} onChange={(e) => { const v = e.target.value as "auto" | "always" | "never"; setShow(v); setLaunchShowPref(v); }} aria-label={t("Launch day show")}>
          <option value="auto">{t("Auto")}</option>
          <option value="always">{t("Always")}</option>
          <option value="never">{t("Never")}</option>
        </select>
      </label>
    </div>
  );
}
